// Client side of in-app purchases: create an embedded Checkout Session, mount
// Stripe's form, confirm the result with our server.
import { STRIPE_PUBLISHABLE_KEY, type ProductKey } from '../config/payments';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';

declare global {
  interface Window { Stripe?: (key: string) => any }
}

const stripeByKey = new Map<string, Promise<any>>();

/**
 * Load Stripe.js from js.stripe.com (Stripe requires it be loaded from there).
 *
 * `publishableKey` should be the one /api/checkout/session returned: the server
 * pairs it with the secret key that created the session (Pages env
 * STRIPE_PUBLISHABLE_KEY), so moving to a new Stripe account is a server env
 * change with no rebuild, and the two can never point at different accounts.
 * The build-time key (src/config/payments.ts) is only the fallback.
 */
export function loadStripeJs(publishableKey?: string): Promise<any> {
  const key = publishableKey || STRIPE_PUBLISHABLE_KEY;
  // No key from the server and none built in: say so instead of letting
  // Stripe.js throw an opaque error deep inside the form.
  if (!key) return Promise.reject(new Error('Payments are unavailable right now. Please try again later.'));
  const cached = stripeByKey.get(key);
  if (cached) return cached;
  const stripePromise = new Promise<any>((resolve, reject) => {
    const done = () => (window.Stripe ? resolve(window.Stripe(key)) : reject(new Error('Stripe failed to load')));
    if (window.Stripe) return done();
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.async = true;
    s.onload = done;
    s.onerror = () => { stripeByKey.delete(key); reject(new Error('Could not reach Stripe. Check your connection.')); };
    document.head.appendChild(s);
  });
  stripeByKey.set(key, stripePromise);
  return stripePromise;
}

export interface EmbeddedSession { clientSecret: string; sessionId: string; publishableKey?: string }

export async function createCheckoutSession(product: ProductKey, returnPath: string): Promise<EmbeddedSession> {
  const res = await fetch('/api/checkout/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({ product, returnPath }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.clientSecret) throw new Error(data.error || 'Could not start checkout.');
  return data;
}

export interface ConfirmResult {
  paid: boolean;
  granted?: boolean;
  kind?: string;
  tier?: string;
  reason?: string;
}

export async function confirmCheckout(sessionId: string): Promise<ConfirmResult> {
  const res = await fetch('/api/checkout/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({ sessionId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not confirm the payment yet.');
  return data;
}

/** Retry confirm for a little while: Stripe can take a moment to mark it paid. */
export async function waitForPaid(sessionId: string, tries = 8): Promise<ConfirmResult> {
  let last: ConfirmResult = { paid: false };
  for (let i = 0; i < tries; i++) {
    try {
      last = await confirmCheckout(sessionId);
      if (last.paid) return last;
    } catch { /* retry */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
  return last;
}
